import styled, { keyframes } from 'styled-components';

import { LUMIO, LUMIO_DESKTOP, LAYOUT_CHROME, MOBILE_MQ, ACQ, ANIM } from '../../../../theme/tokens';

const riseIn = keyframes`
  from { opacity: 0; transform: translateY(14px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

/**
 * A proactive Lumio nudge, floating just above the assistant launcher. Non-
 * modal and dismissable — it invites, never blocks. Sits below the mobile Lumio
 * overlay's z-index so an open assistant always wins.
 *
 * On wide desktop the docked Lumio column occupies the right edge, so the toast
 * steps left of it rather than covering the panel: it appears there whenever the
 * panel's briefing cannot take the notice (a conversation is open, or the notice
 * is one the briefing declines), which is the only reason the toast reaches that
 * viewport at all.
 */
/** Desktop width of the card, also used to offset it from the content edge. */
const TOAST_W = '360px';

export const Toast = styled.div`
  position: fixed;
  right: 20px;
  bottom: 96px;
  z-index: 60;
  width: min(${TOAST_W}, calc(100vw - 40px));
  padding: 16px 16px 14px;
  border-radius: 16px;
  background: ${LUMIO.bg};
  border: 1px solid ${LUMIO.cardBorder};
  box-shadow: ${LUMIO.tipShadow};
  animation: ${riseIn} ${ANIM.MENU_OPEN_MS}ms ${ACQ.ease};

  ${LUMIO_DESKTOP} {
    /* Anchor to the CONTENT CARD's right edge, not the viewport's. The studio
       row is centred inside a max-width container, so a viewport-relative
       offset drifts onto the Lumio column on anything wider than that. The
       layout measures the card and publishes it as --content-* (see
       theme/contentAreaOverlay); the fallback reproduces the plain offset for
       the frame before the first measurement lands. */
    right: auto;
    left: calc(
      var(--content-left, 0px) + var(--content-width, calc(100vw - ${LAYOUT_CHROME.lumioColWidth})) - ${TOAST_W} -
        20px
    );
  }

  @media ${MOBILE_MQ} {
    right: 12px;
    left: 12px;
    bottom: 84px;
    width: auto;
  }
`;

export const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

export const Mark = styled.span`
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.04em;
  background: ${LUMIO.logoText};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

export const Noticed = styled.span`
  font-size: 11px;
  color: ${LUMIO.action};
  letter-spacing: 0.02em;
`;

export const Close = styled.button`
  margin-left: auto;
  appearance: none;
  border: none;
  background: transparent;
  color: ${LUMIO.action};
  font-size: 16px;
  line-height: 1;
  padding: 2px 4px;
  cursor: pointer;

  &:hover {
    color: ${LUMIO.greeting};
  }
`;

export const Message = styled.p`
  margin: 0;
  font-size: 14.5px;
  line-height: 1.5;
  color: ${LUMIO.greeting};
`;

export const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

export const ActionChip = styled.button`
  appearance: none;
  padding: 7px 14px;
  border-radius: 999px;
  border: 1px solid ${LUMIO.cardBorder};
  background: ${LUMIO.cardBg};
  color: ${LUMIO.cardText};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: filter ${ANIM.INTERACT_MS}ms ${ACQ.ease};

  &:hover {
    filter: brightness(1.12);
  }
`;
